import React, { useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useInstructorPermissions } from '../hooks/useInstructorPermissions';
import { DragDropContext } from '@hello-pangea/dnd';
import { db } from '../firebase';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';

// Import custom hooks
import { useScenarioCategories } from '../hooks/scenarioBuilder/useScenarioCategories';
import { useScenarioTemplates } from '../hooks/scenarioBuilder/useScenarioTemplates';

// Import components
import { 
  CategoryModal, 
  DeleteConfirmModal, 
  PreviewModal 
} from '../components/scenarioBuilder';

// Import utilities
import { handleDragEnd, removeScenarioFromCategory } from '../utils/scenarioBuilder/dragDropHandlers';

// Import the main views
import ScenarioList from '../components/scenarioBuilder/ScenarioList';
import ScenarioEditor from '../components/scenarioBuilder/ScenarioEditor';

const ScenarioBuilder = () => {
  const { templateId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, userRole } = useAuth();
  const { permissions, loading: permissionsLoading } = useInstructorPermissions(currentUser?.uid);

  console.log('ScenarioBuilder rendered with templateId:', templateId);
  console.log('Current pathname:', location.pathname);
  
  // Determine if we're creating a new scenario
  const isNewScenario = location.pathname === '/scenario-builder/new';

  // Authorization check
  const isAdminOrOwner = ['Admin', 'owner'].includes(userRole);
  const hasScenarioBuilderAccess = isAdminOrOwner || 
    (userRole === 'Instructor' && permissions?.scenarioBuilder && permissions.scenarioBuilder !== 'none') ||
    (userRole === 'Instructor' && permissions?.specialAccess && permissions.specialAccess.includes('scenarioBuilder'));

  // Debug logging for Maria
  if (currentUser?.email === 'msepulveda8@ccc.edu') {
    console.log('🎭 MARIA SCENARIO BUILDER DEBUG:');
    console.log('- User ID:', currentUser?.uid);
    console.log('- User Role:', userRole);
    console.log('- Raw Permissions:', permissions);
    console.log('- Permissions Loading:', permissionsLoading);
    console.log('- Is Admin/Owner:', isAdminOrOwner);
    console.log('- Has Scenario Builder Access:', hasScenarioBuilderAccess);
  }

  // Custom hooks
  const {
    categories,
    setCategories,
    addCategory,
    updateCategory,
    deleteCategory,
    toggleCategoryExpansion
  } = useScenarioCategories();

  const {
    templates,
    listLoading,
    importing,
    fetchTemplates,
    deleteTemplate,
    importTemplates
  } = useScenarioTemplates();

  // UI state
  const [search, setSearch] = useState("");
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [sortMode, setSortMode] = useState("alpha");

  // Modal states
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categoryEdit, setCategoryEdit] = useState(null);
  const [categoryNameInput, setCategoryNameInput] = useState("");
  const [categoryColorInput, setCategoryColorInput] = useState("#3b82f6");
  const [deleteModal, setDeleteModal] = useState({ open: false, onConfirm: null, message: '' });

  // Color picker state
  const [openPaletteId, setOpenPaletteId] = useState(null);
  const [palettePreview, setPalettePreview] = useState({});

  // Available colors for categories
  const availableColors = [
    '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6',
    '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#84cc16'
  ];

  // Drag and drop handler
  const onDragEnd = (result) => {
    handleDragEnd(result, setCategories);
  };

  // Category modal handlers
  const handleCategoryModalSave = () => {
    if (!categoryNameInput.trim()) return;
    
    if (categoryEdit) {
      updateCategory(categoryEdit.id, categoryNameInput, categoryColorInput);
    } else {
      addCategory(categoryNameInput, categoryColorInput);
    }
    
    setShowCategoryModal(false);
    setCategoryEdit(null);
    setCategoryNameInput("");
    setCategoryColorInput("#3b82f6");
  };

  const handleCategoryEdit = (category) => {
    setCategoryEdit(category);
    setCategoryNameInput(category.name);
    setCategoryColorInput(category.color || '#3b82f6');
    setShowCategoryModal(true);
  };

  // Delete handlers
  const handleDeleteTemplate = (id, name) => {
    setDeleteModal({
      open: true,
      message: `Are you sure you want to delete "${name}"?`,
      onConfirm: async () => {
        await deleteTemplate(id);
        setDeleteModal({ open: false, onConfirm: null, message: '' });
      }
    });
  };

  // Duplicate handler
  const handleDuplicateTemplate = async (template) => {
    try {
      const newName = `${template.name} (Copy)`;
      
      // Create a copy without the id field
      const { id, ...templateWithoutId } = template;
      
      const duplicatedTemplate = {
        ...templateWithoutId,
        name: newName,
        createdBy: currentUser?.email || 'Unknown',
        creationDate: new Date().toLocaleDateString(),
        lastModified: serverTimestamp(),
      };
      
      // Save the duplicated template to Firebase
      const docRef = await addDoc(collection(db, 'scenarioTemplates'), duplicatedTemplate);
      
      // Navigate to edit the new template
      navigate(`/scenario-builder/${docRef.id}`);
      
      // Refresh the templates list
      await fetchTemplates();
    } catch (error) {
      console.error('Error duplicating template:', error);
      alert('Error duplicating scenario: ' + error.message);
    }
  };

  // Show ScenarioEditor for new scenario
  if (isNewScenario) {
    return <ScenarioEditor templateId="new" onSave={fetchTemplates} />;
  }
  
  // Show ScenarioEditor if templateId is present
  if (templateId) {
    return <ScenarioEditor templateId={templateId} onSave={fetchTemplates} />;
  }

  // Show ScenarioList if no templateId
  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <ScenarioList
        templates={templates}
        categories={categories}
        search={search}
        setSearch={setSearch}
        sortMode={sortMode}
        setSortMode={setSortMode}
        listLoading={listLoading}
        importing={importing}
        availableColors={availableColors}
        openPaletteId={openPaletteId}
        setOpenPaletteId={setOpenPaletteId}
        palettePreview={palettePreview}
        setPalettePreview={setPalettePreview}
        onCategoryEdit={handleCategoryEdit}
        onCategoryDelete={deleteCategory}
        onCategoryToggle={toggleCategoryExpansion}
        onCategoryColorChange={updateCategory}
        onScenarioRemove={(catId, tid) => removeScenarioFromCategory(catId, tid, setCategories)}
        onTemplatePreview={setPreviewTemplate}
        onTemplateDelete={handleDeleteTemplate}
        onTemplateDuplicate={handleDuplicateTemplate}
        onImportTemplates={importTemplates}
        onShowCategoryModal={() => setShowCategoryModal(true)}
      />

      {/* Modals */}
      <CategoryModal
        isOpen={showCategoryModal}
        onClose={() => setShowCategoryModal(false)}
        categoryEdit={categoryEdit}
        categoryNameInput={categoryNameInput}
        setCategoryNameInput={setCategoryNameInput}
        categoryColorInput={categoryColorInput}
        setCategoryColorInput={setCategoryColorInput}
        onSave={handleCategoryModalSave}
      />

      <PreviewModal
        template={previewTemplate}
        onClose={() => setPreviewTemplate(null)}
      />

      <DeleteConfirmModal
        isOpen={deleteModal.open}
        message={deleteModal.message}
        onConfirm={deleteModal.onConfirm}
        onCancel={() => setDeleteModal({ open: false, onConfirm: null, message: '' })}
      />
    </DragDropContext>
  );
};

export default ScenarioBuilder;
